import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BLOCKS, WIDTHS, WIDTH_LABEL, HEIGHT_LABEL } from "@/lib/dashboardBlocks";

function Chips({ options, value, onPick, multi, testid }) {
  const on = (v) => (multi ? (value || []).includes(v) : value === v);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([v, l]) => (
        <button key={String(v)} type="button" onClick={() => onPick(v)} data-testid={`${testid}-${v || "all"}`}
          className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            on(v) ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:border-slate-300")}>
          {multi && on(v) && value.length > 1 ? `${value.indexOf(v) + 1}. ` : ""}{l}
        </button>
      ))}
    </div>
  );
}

function Field({ f, value, set }) {
  const tid = `block-setting-${f.key}`;
  if (f.type === "toggle") return <Switch checked={!!value} onCheckedChange={set} data-testid={tid} />;
  if (f.type === "number") return <Input type="number" min={1} max={50} value={value || ""} onChange={(e) => set(Number(e.target.value) || "")} className="w-24" data-testid={tid} />;
  if (f.type === "multi") return <Chips multi options={f.options} value={value || []} testid={tid} onPick={(v) => set((value || []).includes(v) ? value.filter((x) => x !== v) : [...(value || []), v])} />;
  return <Chips options={f.options} value={value} testid={tid} onPick={set} />;
}

function Row({ label, children }) {
  return <div className="space-y-1.5"><p className="text-xs font-semibold text-slate-600">{label}</p>{children}</div>;
}

export function BlockSettingsDialog({ block, onClose, onSave }) {
  const [draft, setDraft] = useState(block);
  useEffect(() => setDraft(block), [block]);
  if (!block) return null;
  const meta = BLOCKS[block.type];
  const setS = (k) => (v) => setDraft((d) => ({ ...d, settings: { ...d.settings, [k]: v } }));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="block-settings-dialog">
        <DialogHeader><DialogTitle>{meta.label} settings</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Row label="Title"><Input value={draft.title || ""} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="block-setting-title" /></Row>
          <Row label="Width"><Chips options={WIDTHS.map((w) => [w, WIDTH_LABEL[w]])} value={draft.w} testid="block-setting-width" onPick={(w) => setDraft((d) => ({ ...d, w }))} /></Row>
          {!meta.autoHeight && <Row label="Height"><Chips options={Object.entries(HEIGHT_LABEL)} value={draft.h} testid="block-setting-height" onPick={(h) => setDraft((d) => ({ ...d, h }))} /></Row>}
          {meta.fields.map((f) => (
            <Row key={f.key} label={f.label}><Field f={f} value={draft.settings?.[f.key]} set={setS(f.key)} /></Row>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="block-settings-cancel">Cancel</Button>
          <Button onClick={() => onSave(draft)} data-testid="block-settings-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
